import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { Box, Paper, Table, Text } from '@mantine/core';
import type { GridAssistHandler } from '@/shared/assist/GridAssistContract';
import { buildDisplayRows, deriveEnumOptions, filterRows, inferColumnFilter, sortRows } from './MegaGridDerivation';
import { useMegaGridState, type MegaGridController } from './useMegaGridState';
import { applyAssistPlan, buildAssistContext } from './MegaGridAssistApply';
import MegaGridAssistBar from './MegaGridAssistBar';
import MegaGridHeader from './MegaGridHeader';
import MegaGridToolbar from './MegaGridToolbar';
import MegaGridFilterControls from './MegaGridFilterControls';
import MegaGridGroupRow from './MegaGridGroupRow';
import MegaGridPaginationFooter from './MegaGridPaginationFooter';
import { exportCsv } from './MegaGridExport';
import type { MegaGridColumn, MegaGridDensity, MegaGridExternalFilter, MegaGridFacet, MegaGridGroupByPill, MegaGridPagination, MegaGridSort, MegaGridViewState } from './MegaGridTypes';

export type { MegaGridColumn, MegaGridExternalFilter, MegaGridFacet, MegaGridGroupByPill, MegaGridPagination, MegaGridViewState, MegaGridFilterInstance, MegaGridFilterOp } from './MegaGridTypes';
export { useMegaGridState } from './useMegaGridState';
export type { MegaGridController } from './useMegaGridState';

/** Opts the grid into the built-in AI assist bar. The handler is the brain (network lives with the host). */
export interface MegaGridAssistOptions {
    handler: GridAssistHandler;
    /** Dataset description shown to the model, e.g. "AI interaction turns". */
    title?: string;
    placeholder?: string;
}

export interface MegaGridProps<T> {
    columns: MegaGridColumn<T>[];
    rows: T[];
    getRowKey: (row: T, index: number) => string | number;
    searchAccessor?: (row: T) => string;
    searchPlaceholder?: string;
    /** Legacy faceted toolbar filters (the old `DataTableFilter`). */
    filters?: MegaGridFacet<T>[];
    /** Host-owned filters already constraining `rows` (e.g. the app's global filters); listed read-only in the filters dropdown. */
    externalFilters?: MegaGridExternalFilter[];
    toolbar?: ReactNode;
    emptyMessage?: string;
    noMatchMessage?: string;
    maxHeight?: number | string;
    minWidth?: number;
    onRowClick?: (row: T) => void;
    /** Clicking a group header's label invokes this (e.g. to filter by that group); the chevron still toggles expansion. `rows` are the group's leaf rows. */
    onGroupClick?: (key: string, depth: number, rows: T[]) => void;
    pagination?: MegaGridPagination;
    /** Internal client-side pagination at this page size (ignored with `pagination` or grouping). */
    clientPageSize?: number;
    loading?: boolean;
    /** Persists view state + enables saved views under this key. */
    persistenceKey?: string;
    /** @deprecated Striping was removed to match the brand grid look; this prop is now a no-op. */
    striped?: boolean;
    density?: MegaGridDensity;
    defaultGroupBy?: string[];
    enableGrouping?: boolean;
    /** Replaces the group-by popover editor with a flat row of these definitive group-by pills. */
    groupByPills?: MegaGridGroupByPill[];
    allowMultiSort?: boolean;
    /** Seeds the grid's default sort (overridden by persisted/user state). */
    defaultSort?: MegaGridSort;
    allowPinning?: boolean;
    enableColumnSelector?: boolean;
    enableExport?: boolean;
    exportName?: string;
    /** Hides the top toolbar entirely (column headers keep sort + filter). */
    hideToolbar?: boolean;
    /** Renders a right-aligned summary (e.g. a subtotal) in each group header row. */
    groupSummary?: (rows: T[]) => ReactNode;
    /** Enables the natural-language assist bar, driven by the supplied handler. */
    assist?: MegaGridAssistOptions;
    /**
     * External grid controller (from `useMegaGridState`). When provided the
     * caller owns the view state and persistence — pass `persistenceKey` to the
     * hook, not to the grid (the grid-level key only keeps the saved-views UI enabled).
     */
    controller?: MegaGridController;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

const COUNT_FORMAT = new Intl.NumberFormat('en-US');

function pinnedStyle(pinned: boolean, offset: number): CSSProperties {
    if (!pinned) return {};
    return { position: 'sticky', left: offset, zIndex: 1, background: 'var(--bm-color-surface)' };
}

export default function MegaGrid<T>({
    columns,
    rows,
    getRowKey,
    searchAccessor,
    searchPlaceholder = 'Search',
    filters = [],
    externalFilters = [],
    toolbar,
    emptyMessage = 'No data',
    noMatchMessage = 'No matches',
    maxHeight = 'calc(100vh - 240px)',
    minWidth = 560,
    onRowClick,
    onGroupClick,
    pagination,
    clientPageSize,
    loading = false,
    persistenceKey,
    density = 'comfortable',
    defaultGroupBy,
    enableGrouping = true,
    groupByPills,
    allowMultiSort = true,
    defaultSort,
    allowPinning = true,
    enableColumnSelector = true,
    enableExport = true,
    exportName = 'export',
    hideToolbar = false,
    groupSummary,
    assist,
    controller: externalController,
}: MegaGridProps<T>): ReactElement {
    const internalController = useMegaGridState({
        columns,
        persistenceKey: externalController === undefined ? persistenceKey : undefined,
        defaultDensity: density,
        defaultGroupBy: defaultGroupBy ?? [],
        allowMultiSort,
        ...(defaultSort ? { defaultSort } : {}),
    });
    const controller = externalController ?? internalController;
    const { state } = controller;

    const [assistBusy, setAssistBusy] = useState(false);
    const [assistNarrative, setAssistNarrative] = useState<string | null>(null);
    const [assistError, setAssistError] = useState<string | null>(null);
    const assistUndo = useRef<MegaGridViewState | null>(null);

    const [clientPage, setClientPage] = useState(1);
    const [clientSize, setClientSize] = useState<number>(clientPageSize ?? 25);

    const effectiveColumns = useMemo(
        () =>
            columns.map((c): MegaGridColumn<T> => {
                const filter = inferColumnFilter(c, rows);
                return filter === c.filter ? c : { ...c, filter };
            }),
        [columns, rows],
    );

    const byKey = useMemo(() => new Map(effectiveColumns.map((c) => [c.key, c])), [effectiveColumns]);
    const hidden = useMemo(() => new Set(state.hidden), [state.hidden]);
    const pinned = useMemo(() => new Set(state.pinned), [state.pinned]);

    const visibleColumns = useMemo(() => {
        const visible = state.columnOrder.map((k) => byKey.get(k)).filter((c): c is MegaGridColumn<T> => Boolean(c) && !hidden.has(c!.key));
        return [...visible.filter((c) => pinned.has(c.key)), ...visible.filter((c) => !pinned.has(c.key))];
    }, [state.columnOrder, byKey, hidden, pinned]);

    const pinnedOffsets = useMemo(() => {
        const offsets: Record<string, number> = {};
        let acc = 0;
        for (const col of visibleColumns) {
            if (!pinned.has(col.key)) continue;
            offsets[col.key] = acc;
            acc += state.widths[col.key] ?? (typeof col.width === 'number' ? col.width : 160);
        }
        return offsets;
    }, [visibleColumns, pinned, state.widths]);

    const enumOptions = useMemo(() => {
        const out: Record<string, { label: string; value: string }[]> = {};
        for (const col of effectiveColumns) if (col.filter?.type === 'enum') out[col.key] = deriveEnumOptions(rows, col);
        return out;
    }, [effectiveColumns, rows]);

    const filtered = useMemo(
        () => filterRows(rows, effectiveColumns, { search: state.search, searchAccessor, filters: state.filters, facets: filters, facetValues: state.facets }),
        [rows, effectiveColumns, state.search, searchAccessor, state.filters, filters, state.facets],
    );
    const sorted = useMemo(() => sortRows(filtered, effectiveColumns, state.sorts), [filtered, effectiveColumns, state.sorts]);

    const grouping = state.groupBy.length > 0;
    const clientPaging = clientPageSize !== undefined && !pagination && !grouping;
    const totalPages = clientPaging ? Math.max(1, Math.ceil(sorted.length / clientSize)) : 1;
    const page = Math.min(clientPage, totalPages);
    const pageRows = clientPaging ? sorted.slice((page - 1) * clientSize, page * clientSize) : sorted;

    useEffect(() => {
        setClientPage(1);
    }, [state.search, state.filters, state.facets, clientSize, state.groupBy]);

    const display = useMemo(() => buildDisplayRows(pageRows, effectiveColumns, state.groupBy, controller.expanded), [pageRows, effectiveColumns, state.groupBy, controller.expanded]);

    const groupableColumns = useMemo(() => effectiveColumns.filter((c) => !c.noGroup && (c.groupValue || c.sortValue)), [effectiveColumns]);
    const compact = state.density === 'compact';
    const countLabel = pagination ? COUNT_FORMAT.format(pagination.total) : `${COUNT_FORMAT.format(sorted.length)} of ${COUNT_FORMAT.format(rows.length)}`;

    const handleExport = (): void => exportCsv(visibleColumns, sorted, exportName);

    const handleAssistAsk = async (prompt: string): Promise<void> => {
        if (assist === undefined) return;
        setAssistBusy(true);
        setAssistError(null);
        try {
            const context = buildAssistContext(effectiveColumns, state, rows, controller.views, controller.activeViewId, assist.title);
            const plan = await assist.handler({ prompt, grid: context });
            assistUndo.current = structuredClone(state);
            applyAssistPlan(plan, controller, effectiveColumns);
            setAssistNarrative(plan.narrative);
        } catch {
            setAssistError('The assistant could not process that request — try rephrasing.');
        } finally {
            setAssistBusy(false);
        }
    };

    const handleAssistRevert = (): void => {
        if (assistUndo.current === null) return;
        controller.applyStatePatch(assistUndo.current);
        assistUndo.current = null;
        setAssistNarrative(null);
    };

    return (
        <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            {!hideToolbar && assist !== undefined && (
                <MegaGridAssistBar
                    placeholder={assist.placeholder ?? 'Ask the grid — “group by department, hide the model column, save as…”'}
                    busy={assistBusy}
                    narrative={assistNarrative}
                    error={assistError}
                    canRevert={assistUndo.current !== null}
                    onAsk={(prompt) => void handleAssistAsk(prompt)}
                    onRevert={handleAssistRevert}
                />
            )}
            {!hideToolbar && (
                <MegaGridToolbar
                    controller={controller}
                    columns={effectiveColumns}
                    groupableColumns={groupableColumns}
                    groupByPills={groupByPills}
                    hasSearch={Boolean(searchAccessor)}
                    searchPlaceholder={searchPlaceholder}
                    facets={filters}
                    filterControls={
                        <MegaGridFilterControls
                            columns={effectiveColumns}
                            filters={state.filters}
                            externalFilters={externalFilters}
                            enumOptions={enumOptions}
                            onAdd={controller.addFilter}
                            onUpdate={controller.updateFilter}
                            onRemove={controller.removeFilter}
                            onClear={controller.clearFilters}
                        />
                    }
                    customToolbar={toolbar}
                    countLabel={countLabel}
                    enableColumnSelector={enableColumnSelector}
                    enableGrouping={enableGrouping}
                    enableSavedViews={Boolean(persistenceKey) || externalController !== undefined}
                    allowPinning={allowPinning}
                    onExport={enableExport ? handleExport : undefined}
                />
            )}

            <Box style={{ maxHeight, overflow: 'auto' }}>
                <Table
                    highlightOnHover
                    stickyHeader
                    withColumnBorders={false}
                    miw={minWidth}
                    verticalSpacing={compact ? 4 : undefined}
                    horizontalSpacing={compact ? 8 : undefined}
                >
                    <MegaGridHeader
                        columns={visibleColumns}
                        sorts={state.sorts}
                        widths={state.widths}
                        pinned={pinned}
                        pinnedOffsets={pinnedOffsets}
                        density={state.density}
                        allowResize
                        onToggleSort={controller.toggleSort}
                        onResize={controller.setColumnWidth}
                    />
                    <Table.Tbody>
                        {display.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={visibleColumns.length}>
                                    <Text size="sm" c="dimmed" ta="center" py="lg">
                                        {loading ? 'Loading…' : rows.length === 0 ? emptyMessage : noMatchMessage}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ) : (
                            display.map((item) =>
                                item.kind === 'group' ? (
                                    <MegaGridGroupRow
                                        key={item.id}
                                        label={item.label}
                                        count={item.count}
                                        depth={item.depth}
                                        collapsed={!controller.expanded.has(item.id)}
                                        colSpan={visibleColumns.length}
                                        columns={visibleColumns}
                                        rows={item.rows}
                                        onToggle={() => controller.toggleGroup(item.id)}
                                        onSelect={onGroupClick ? (): void => onGroupClick(item.label, item.depth, item.rows) : undefined}
                                        summary={groupSummary?.(item.rows)}
                                    />
                                ) : (
                                    <Table.Tr
                                        key={getRowKey(item.row, item.index)}
                                        onClick={onRowClick ? () => onRowClick(item.row) : undefined}
                                        onKeyDown={
                                            onRowClick
                                                ? (e) => {
                                                      if (e.key === 'Enter' || e.key === ' ') {
                                                          e.preventDefault();
                                                          onRowClick(item.row);
                                                      }
                                                  }
                                                : undefined
                                        }
                                        role={onRowClick ? 'button' : undefined}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        style={onRowClick ? { cursor: 'pointer' } : undefined}
                                    >
                                        {visibleColumns.map((col) => (
                                            <Table.Td
                                                key={col.key}
                                                style={{
                                                    textAlign: col.align ?? 'left',
                                                    fontSize: compact ? 11 : undefined,
                                                    ...pinnedStyle(pinned.has(col.key), pinnedOffsets[col.key] ?? 0),
                                                }}
                                            >
                                                {col.render(item.row)}
                                            </Table.Td>
                                        ))}
                                    </Table.Tr>
                                ),
                            )
                        )}
                    </Table.Tbody>
                </Table>
            </Box>

            {pagination && <MegaGridPaginationFooter pagination={pagination} loading={loading} />}
            {clientPaging && (
                <MegaGridPaginationFooter
                    pagination={{
                        page,
                        pageSize: clientSize,
                        total: sorted.length,
                        totalPages,
                        onPageChange: setClientPage,
                        onPageSizeChange: setClientSize,
                        pageSizeOptions: Array.from(new Set([clientPageSize ?? 25, ...DEFAULT_PAGE_SIZE_OPTIONS])).sort((a, b): number => a - b),
                    }}
                    loading={loading}
                />
            )}
        </Paper>
    );
}
